import { Module, PATH_PROJECT_WITH_ID } from "@/constants";
import {
  basicListingProject,
  fullListingProject,
  PROJECT_STATUS,
  PROJECT_STATUS_DELETED,
} from "@/schemas/project";
import { ReadProject } from "@/types/project";
import { Row } from "@tanstack/react-table";
import i18n from "@/i18n";
import StandardQueryTable from "@/components/StandardQueryTable";
import { ProjectService } from "../service/project";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { PROJECT_DEPENDS_ON } from "@/schemas/commonFields";
import { generatePath } from "react-router-dom";
import { useMemo } from "react";
import { getRowPropsWithDeletedHighlight } from "@/lib/table/getRowPropsWithDeletedHighlight";

const ProjectList = () => {
  const permissions = useModulePermissions(Module.Projects);
  const schema = permissions.projects_read_full
    ? fullListingProject
    : basicListingProject;

  const columnOverrides = {
    ...(permissions.projects_read_full && {
      status: {
        id: "status",
        cell: ({ row }: { row: Row<ReadProject> }) =>
          i18n.t(`projectStatuses.${row.original.status}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_STATUS,
              translationKey: "projectStatuses",
            },
          },
        },
      },
      project_depends_on: {
        id: "project_depends_on",
        cell: ({ row }: { row: Row<ReadProject> }) =>
          i18n.t(`projectDependsOn.${row.original.project_depends_on}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_DEPENDS_ON,
              translationKey: "projectDependsOn",
            },
          },
        },
      },
    }),
  };

  const tableFeatures = useMemo(
    () => ({
      exportSettings: { moduleName: Module.Projects },
      enableShowDeletedToggle: true,
      getRowProps: getRowPropsWithDeletedHighlight<ReadProject>(
        (row) => row.status === PROJECT_STATUS_DELETED,
      ),
    }),
    [],
  );

  return (
    <StandardQueryTable<ReadProject>
      schema={schema}
      name={Module.Projects}
      fetchList={ProjectService.getList}
      pathBuilder={(row) =>
        generatePath(PATH_PROJECT_WITH_ID, {
          projectId: row.id,
        })
      }
      tableFeatures={tableFeatures}
      columnOverrides={columnOverrides}
    />
  );
};

export default ProjectList;
